/**
 * @fileoverview Syndicator Showdown — Pricing Engine v4
 * @module shared/premiumCurve
 *
 * ─── FORMULA UPGRADE (v4) ────────────────────────────────────────────────────
 * New: basePrice = K × (avgSpendPerRace / 100) × (1/odds)^γ
 *      γ = 0.991  |  K default = 2.900
 *
 * Pricing is now anchored to the average spend per race (what players
 * actually invest), NOT the prize pool (which is irrelevant to price
 * calibration). A game with the same avgSpend produces identical prices
 * regardless of what prizes are on offer.
 *
 * Tiered tranches (replaces flat TIER_BANDS):
 *   T1 first 30% @ 1.00×  T2 next 25% @ 1.10×  T3 next 20% @ 1.25×
 *   T4 next 15% @ 1.50×   T5 last 10% @ 2.00×
 *
 * All original function signatures are PRESERVED for backward compatibility.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { MIN_RUNNERS_PER_RACE, MAX_FIELD_COVERAGE } from './constants.js';

// ─── Pricing constants ────────────────────────────────────────────────────────

export const GAMMA                = 0.991;
export const DEFAULT_K_MULTIPLIER = 2.900;

// New tiered tranche structure
export const TRANCHES = [
  { size: 30, mult: 1.00, label: 'T1' },
  { size: 25, mult: 1.10, label: 'T2' },
  { size: 20, mult: 1.25, label: 'T3' },
  { size: 15, mult: 1.50, label: 'T4' },
  { size: 10, mult: 2.00, label: 'T5' },
];

// ─── Core share price calculation (v4 formula) ───────────────────────────────

/**
 * Base price per 1% equity share using the spend-anchored power-law formula.
 *
 *   basePrice = K × (avgSpendPerRace / 100) × (1/odds)^γ   γ=0.991, K default=2.900
 *
 * Price is proportional to average player spend per race, not prize money.
 * fieldSize and raceCount are kept for API compatibility but unused in the formula.
 */
export function calculateBaseSharePrice(
  odds,
  fieldSize        = 8,
  raceCount        = 8,
  avgSpendPerRace  = 100_000,
  kMultiplier      = DEFAULT_K_MULTIPLIER,
) {
  if (!odds || odds <= 1 || !avgSpendPerRace) return 0;
  const rawPrice = (kMultiplier * avgSpendPerRace * Math.pow(1 / odds, GAMMA)) / 100;
  return Math.max(5, Math.round(rawPrice / 5) * 5);
}

// Alias used by the new calculator and admin odds page
export function calcBaseSharePrice(odds, avgSpendPerRace, kMultiplier = DEFAULT_K_MULTIPLIER) {
  return calculateBaseSharePrice(odds, 8, 8, avgSpendPerRace, kMultiplier);
}

// ─── Tier band multiplier (now uses TRANCHES, not TIER_BANDS) ─────────────────

export function getTierMultiplier(equityPercent) {
  let cumulative = 0;
  for (const t of TRANCHES) {
    cumulative += t.size;
    if (equityPercent < cumulative) return t.mult;
  }
  return TRANCHES[TRANCHES.length - 1].mult;
}

// ─── Purchase cost calculation (ORIGINAL signature preserved) ────────────────

/**
 * Cost to buy `sharesToBuy` percent starting from `currentEquity`.
 * Applies the new tiered multipliers step by step.
 */
export function calculatePurchaseCost(currentEquity, sharesToBuy, basePrice) {
  if (!sharesToBuy || sharesToBuy <= 0) return 0;
  if (!basePrice   || basePrice   <= 0) return 0;
  let totalCost = 0;
  for (let i = 0; i < sharesToBuy; i++) {
    const equityAtStep = currentEquity + i;
    totalCost += basePrice * getTierMultiplier(equityAtStep);
  }
  return Math.round(totalCost);
}

// ─── New tranche-based helpers (used by admin odds page + calculator) ─────────

export function buildTranches(basePrice) {
  let cumShares = 0;
  let cumCost   = 0;
  return TRANCHES.map(t => {
    const start         = cumShares;
    const end           = cumShares + t.size;
    const pricePerShare = basePrice * t.mult;
    const trancheCost   = t.size * pricePerShare;
    const result = { ...t, start, end, pricePerShare, trancheCost, cumCostAtStart: cumCost };
    cumShares = end;
    cumCost  += trancheCost;
    return result;
  });
}

export function costForShares(shares, tranches) {
  let remaining = Math.min(shares, 100);
  let total = 0;
  for (const t of tranches) {
    if (remaining <= 0) break;
    const inThisTranche = Math.min(remaining, t.size);
    total     += inThisTranche * t.pricePerShare;
    remaining -= inThisTranche;
  }
  return total;
}

export function equityForBudget(budget, tranches) {
  let remaining = budget;
  let shares    = 0;
  for (const t of tranches) {
    if (remaining <= 0) break;
    const maxCost = t.size * t.pricePerShare;
    if (remaining >= maxCost) {
      shares    += t.size;
      remaining -= maxCost;
    } else {
      shares    += remaining / t.pricePerShare;
      remaining  = 0;
    }
  }
  return Math.min(shares, 100);
}

export function totalHorseCost(tranches) {
  return tranches.reduce((sum, t) => sum + t.trancheCost, 0);
}

export function getRunnerPricing(odds, avgSpendPerRace, kMultiplier = DEFAULT_K_MULTIPLIER) {
  const basePrice = calcBaseSharePrice(odds, avgSpendPerRace, kMultiplier);
  if (!basePrice) return null;
  const tranches = buildTranches(basePrice);
  return { basePrice, tranches, totalCost: totalHorseCost(tranches) };
}

// ─── Compliance helpers (ORIGINAL signatures preserved) ──────────────────────

export function calculatePerRaceFloor(avgSpend, raceCount) {
  if (!raceCount || raceCount <= 0) return avgSpend;
  return Math.round((avgSpend * 20) / (20 + raceCount) / 1000) * 1000;
}

export function calculateMaxPerRace(totalBankroll, raceCount, perRaceFloor) {
  if (!raceCount || raceCount <= 1) return totalBankroll;
  return Math.max(0, totalBankroll - (raceCount - 1) * perRaceFloor);
}

// ─── Dividend calculation (ORIGINAL preserved) ───────────────────────────────

export function calculateDividend(equityPercent, prizeAmount) {
  if (!equityPercent || !prizeAmount) return 0;
  return Math.round((equityPercent / 100) * prizeAmount);
}

/**
 * Adjusts a prize pool object for dead heats.
 * Combined prize of shared positions is split equally.
 */
export function adjustPrizePoolForDeadHeat(placements, prizePool) {
  const positionKeys = ['first', 'second', 'third', 'fourth', 'fifth'];
  const pool         = { ...prizePool };
  const placeCounts  = {};

  placements.forEach(p => {
    if (p.position >= 1 && p.position <= 5) {
      placeCounts[p.position] = (placeCounts[p.position] || 0) + 1;
    }
  });

  Object.entries(placeCounts).forEach(([position, count]) => {
    if (count <= 1) return;
    const p             = parseInt(position);
    const affectedKeys  = Array.from({ length: count }, (_, i) => positionKeys[p - 1 + i]).filter(Boolean);
    const combinedPrize = affectedKeys.reduce((sum, k) => sum + (pool[k] || 0), 0);
    const eachPrize     = Math.round(combinedPrize / count);
    affectedKeys.forEach(k => { pool[k] = eachPrize; });
  });

  return pool;
}

// ─── Field pricing ────────────────────────────────────────────────────────────

/**
 * Returns an array of price objects for each non-scratched runner.
 * Each item: { id, baseSharePrice, maxOwnershipPct }
 *
 * @param {Array}  runners          - Runner objects (handles null/undefined safely)
 * @param {number} avgSpendPerRace  - Average player spend per race (default $100k)
 * @param {number} kMultiplier      - Pricing curve multiplier
 * @returns {Array<{ id: string, baseSharePrice: number, maxOwnershipPct: number }>}
 */
export function calculateFieldPrices(runners, avgSpendPerRace = 100_000, kMultiplier = DEFAULT_K_MULTIPLIER) {
  const safeRunners = Array.isArray(runners) ? runners : [];
  const active = safeRunners.filter(r => r.status?.toLowerCase() !== 'scratched');

  // Per-runner ownership ceiling: a player may buy up to 100% of any single runner.
  // This is independent of the field coverage cap (MAX_FIELD_COVERAGE), which limits
  // the maximum number of *different* runners a player can hold in a race (50% of
  // field size) — enforced separately in BuyModal via runnersCurrentlyHeld.
  const maxOwnershipPct = 100;

  return active.map(runner => ({
    id: runner.id,
    baseSharePrice: (!runner.odds || runner.odds <= 1)
      ? 0
      : calculateBaseSharePrice(
          runner.odds,
          active.length,
          8,
          avgSpendPerRace,
          kMultiplier,
        ),
    maxOwnershipPct,
  }));
}

// ─── Slider steps (ORIGINAL implementations preserved) ───────────────────────

export function generateSliderSteps(currentEquity = 0) {
  const maxEquity = Math.floor(MAX_FIELD_COVERAGE * 100);
  const steps     = [];
  for (let i = 0; i <= maxEquity; i++) {
    steps.push(i);
  }
  return steps;
}

export function snapToSliderStep(equity) {
  return Math.max(0, Math.min(Math.round(equity), Math.floor(MAX_FIELD_COVERAGE * 100)));
}

export function isValidSliderPosition(equity) {
  return Number.isInteger(equity) && equity >= 0 && equity <= Math.floor(MAX_FIELD_COVERAGE * 100);
}

export const SLIDER_STEPS = generateSliderSteps();

// ─── Legacy alias ─────────────────────────────────────────────────────────────

export function getSharePrice(odds, avgSpendPerRace, kMultiplier = DEFAULT_K_MULTIPLIER) {
  return calcBaseSharePrice(odds, avgSpendPerRace, kMultiplier);
}